import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Globe, Check } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '../i18n';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LanguageSelector = ({ variant = 'dropdown', showLabel = true }) => {
  const { i18n, t } = useTranslation();

  const handleLanguageChange = async (langCode) => {
    try {
      // Change language in i18n
      await i18n.changeLanguage(langCode);
      
      // Save to localStorage
      localStorage.setItem('preferredLanguage', langCode);
      
      // Save to backend if user is logged in
      const token = localStorage.getItem('token');
      if (token) {
        try {
          await axios.put(
            `${API_URL}/auth/language`,
            { language: langCode },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (err) {
          console.log('Could not save language to server');
        }
      }
      
      toast.success(t('profile.languageSaved'));
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  if (variant === 'list') {
    return (
      <div className="space-y-2">
        {SUPPORTED_LANGUAGES.map((lang) => (
          <Button
            key={lang.code}
            variant={i18n.language === lang.code ? 'default' : 'outline'}
            className="w-full justify-between"
            onClick={() => handleLanguageChange(lang.code)}
            data-testid={`lang-${lang.code}`}
          >
            <span>{lang.nativeName}</span>
            {i18n.language === lang.code && <Check className="w-4 h-4" />}
          </Button>
        ))}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2" data-testid="language-selector">
          <Globe className="w-4 h-4" />
          {showLabel && <span>{currentLang.nativeName}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className="flex justify-between cursor-pointer"
            data-testid={`lang-option-${lang.code}`}
          >
            <span>{lang.nativeName}</span>
            {i18n.language === lang.code && <Check className="w-4 h-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
